/** 포인트 잔액 · 내역의 웹 클라이언트(`W-70`). **읽기만 한다.**
 *
 * 서버: `apps/api/src/routes/points.ts` 의 `GET /api/points/balance` · `GET /api/points/history`(PR #244).
 * 요청은 전부 `api.ts` 의 `request` 를 거친다. **401 이면 세션 게이트가 깨어난다.**
 *
 * ⚠️ 경로와 응답 모양이 아직 `@makit/shared` 에 없다(라우트 파일의 지역 타입 `PointBalanceResponse` ·
 * `PointHistoryResponse`). 아래 두 타입과 경로 두 줄이 그 자리를 잡고 있다. `data-manager` 가 shared 로
 * 올리면(`pointsEndpoints` 같은 이름으로) 이 파일의 지역 타입과 손으로 적은 경로를 지운다.
 *
 * ⛔ 웹은 포인트를 팔지 않는다(PRD 「2026-10-25 둘째 개정」). 충전 · 충전 확인 · 계정 표지 · 포인트로
 * 결제 호출을 이 파일에 두지 않는다. 웹 포인트 결제(`W-26`)는 `13.1` O-89 뒤 회차다.
 */

import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import type { PointBalanceView, PointChargePackView, PointHistoryItemView } from "@makit/shared";

import { request } from "./api";

export interface PointBalanceResponse {
  balance: PointBalanceView;
  packs: PointChargePackView[];
}

export interface PointHistoryResponse {
  items: PointHistoryItemView[];
  /** 다음 쪽의 `before`. 없으면 끝이다. */
  nextCursor: number | null;
}

/** 한 쪽의 줄 수. 서버 상한(`POINT_HISTORY_MAX_LIMIT` 50)과 같다. 읽기라 장당 과금과 무관하다. */
export const POINT_HISTORY_PAGE_SIZE = 50;

export function fetchPointBalance(): Promise<PointBalanceResponse> {
  return request<PointBalanceResponse>("/api/points/balance");
}

export function fetchPointHistory(before: number | null): Promise<PointHistoryResponse> {
  const query = `limit=${POINT_HISTORY_PAGE_SIZE}${before === null ? "" : `&before=${before}`}`;
  return request<PointHistoryResponse>(`/api/points/history?${query}`);
}

/** 키를 화면마다 적지 않는다. 앱의 `points-api.ts` 와 같은 키다. */
export const pointQueryKeys = {
  balance: ["points", "balance"] as const,
  history: ["points", "history"] as const,
};

export function usePointBalance() {
  return useQuery({ queryKey: pointQueryKeys.balance, queryFn: fetchPointBalance });
}

/** 최신순 내역. 다음 쪽은 화면이 끝에 닿았을 때만 부른다(자동으로 다 읽지 않는다). */
export function usePointHistory() {
  return useInfiniteQuery({
    queryKey: pointQueryKeys.history,
    queryFn: ({ pageParam }) => fetchPointHistory(pageParam),
    initialPageParam: null as number | null,
    getNextPageParam: (last) => last.nextCursor,
  });
}
